import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_pages, get_page_sections, create_section, update_section, delete_section, get_section } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import './TabContent.css';

const SectionsTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listPagesApi = useApi(list_pages);
  const getPageSectionsApi = useApi(get_page_sections);
  const createSectionApi = useApi(create_section);
  const updateSectionApi = useApi(update_section);
  const deleteSectionApi = useApi(delete_section);
  const getSectionApi = useApi(get_section);
  const gridRef = useRef(null);

  const [editingSection, setEditingSection] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allSections, setAllSections] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [loadedPagesCount, setLoadedPagesCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);

  const PAGE_BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchSections = useCallback(async () => {
    const pagesData = await listPagesApi.execute({
      skip: 0,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData) {
      setAllPages(pagesData);
      setLoadedPagesCount(pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const sectionsPromises = pagesData.map(page =>
        getPageSectionsApi.execute({ page_id: page.id }).catch(() => [])
      );

      const sectionsArrays = await Promise.all(sectionsPromises);
      const allSects = sectionsArrays.flat();

      const filteredSects = searchQuery
        ? allSects.filter(sect => {
          const title = getLocalizedField(sect, 'title').toLowerCase();
          const text = getLocalizedField(sect, 'text').toLowerCase();
          return title.includes(searchQuery.toLowerCase()) || text.includes(searchQuery.toLowerCase());
        })
        : allSects;

      setAllSections(filteredSects);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMorePages = useCallback(async () => {
    if (!hasMorePages || listPagesApi.loading) return;

    const pagesData = await listPagesApi.execute({
      skip: loadedPagesCount,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData && pagesData.length > 0) {
      const newPages = [...allPages, ...pagesData];
      setAllPages(newPages);
      setLoadedPagesCount(loadedPagesCount + pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const sectionsPromises = pagesData.map(page =>
        getPageSectionsApi.execute({ page_id: page.id }).catch(() => [])
      );

      const sectionsArrays = await Promise.all(sectionsPromises);
      const newSects = sectionsArrays.flat();

      setAllSections([...allSections, ...newSects]);
    } else {
      setHasMorePages(false);
    }
  }, [allPages, allSections, loadedPagesCount, hasMorePages, listPagesApi.loading]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchSections();
    }
  }, [fetchSections]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingSection(null);
    } else if (id && currentPath.includes('/edit')) {
      const fetchSectionForEdit = async () => {
        const sectionItem = await getSectionApi.execute({ section_id: parseInt(id) });
        if (sectionItem) {
          setEditingSection(sectionItem);
          setIsCreating(false);
        }
      };
      fetchSectionForEdit();
    } else {
      setIsCreating(false);
      setEditingSection(null);
    }
  }, [id, params]);

  useEffect(() => {
    const neededSections = currentPage * itemsPerPage;
    if (neededSections > allSections.length && hasMorePages && !listPagesApi.loading) {
      loadMorePages();
    }
  }, [currentPage, itemsPerPage, allSections.length, hasMorePages, listPagesApi.loading, loadMorePages]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedSections = allSections.slice(startIndex, endIndex);
  const totalPages = Math.ceil(allSections.length / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/sections/new');
  };

  const handleEdit = (sectionItem) => {
    navigate(`/admin/sections/${sectionItem.id}/edit`);
  };

  const handleCancel = () => {
    navigate('/admin/sections');
  };

  const storageKey = isCreating ? 'admin_sections_create' : (id ? `admin_sections_edit_${id}` : null);

  const handleCopy = (sectionItem) => {
    const copyData = {
      title_en: sectionItem.title_en || '', title_cz: sectionItem.title_cz || '', title_de: sectionItem.title_de || '',
      text_en: sectionItem.text_en || '', text_cz: sectionItem.text_cz || '', text_de: sectionItem.text_de || '',
      page_id: sectionItem.page_id || '', private: sectionItem.private || false,
    };
    localStorage.setItem('admin_sections_create', JSON.stringify(copyData));
    navigate('/admin/sections/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createSectionApi.execute(formData);

      if (createSectionApi.error) {
        toast.error(createSectionApi.error || 'An error occurred while creating the section', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Section created successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchSections();
        navigate('/admin/sections');
      }
    } else if (editingSection) {
      const result = await updateSectionApi.execute({
        ...formData,
        section_id: editingSection.id
      });

      if (updateSectionApi.error) {
        toast.error(updateSectionApi.error || 'An error occurred while updating the section', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Section updated successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchSections();
        navigate('/admin/sections');
      }
    }
  };

  const handleDelete = async (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      const result = await deleteSectionApi.execute({ section_id: sectionId });
      if (deleteSectionApi.error) {
        toast.error(deleteSectionApi.error || 'An error occurred while deleting the section', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Section deleted successfully!', {
          position: "bottom-right"
        });
        await fetchSections();
        navigate('/admin/sections');
      }
    }
  };

  const getLocalizedField = (item, fieldPrefix) => {
    const language = i18n.language;
    if (item[`${fieldPrefix}_${language}`]) {
      return item[`${fieldPrefix}_${language}`];
    }
    if (item[`${fieldPrefix}_cz`]) {
      return item[`${fieldPrefix}_cz`];
    }
    if (item[`${fieldPrefix}_en`]) {
      return item[`${fieldPrefix}_en`];
    }
    if (item[`${fieldPrefix}_de`]) {
      return item[`${fieldPrefix}_de`];
    }
    return '';
  };

  const getPageName = (pageId) => {
    const page = allPages.find(p => p.id === pageId);
    if (!page) return `Page #${pageId}`;
    return getLocalizedField(page, 'title') || `Page #${pageId}`;
  };

  const sectionFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.sectionTitle') },
        { name: 'text', label: t('admin.fields.content'), type: 'markdown', required: false, placeholder: t('admin.placeholders.sectionContent') }
      ]
    },
    {
      name: 'page_id',
      label: t('admin.fields.page'),
      type: 'select',
      required: true,
      options: allPages.map(page => ({
        value: page.id,
        label: `#${page.id} - ${getLocalizedField(page, 'title') || 'Untitled Page'}`
      })),
      disabled: !isCreating,
      className: 'page-select-field'
    },
    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false }
  ];

  if (isCreating || editingSection) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.sections.createTitle') : t('admin.sections.editTitle')}</h2>
          <p className="tab-description">
            {isCreating ? t('admin.sections.createDesc') : `${t('admin.sections.editDesc')}: ${getLocalizedField(editingSection, 'title')}`}
          </p>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingSection && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingSection)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingSection && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingSection.id)}
              disabled={deleteSectionApi.loading}
            >
              {deleteSectionApi.loading ? t('common.deleting') : t('admin.sections.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={sectionFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={editingSection || {}}
          submitLabel={isCreating ? t('admin.sections.createBtn') : t('admin.sections.updateBtn')}
          isLoading={createSectionApi.loading || updateSectionApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.sections.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.sections.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.sections.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {(listPagesApi.loading || getPageSectionsApi.loading) && allSections.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading sections...')} />
      ) : allSections.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          <p>{t('admin.sections.noSections')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.sections.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: (listPagesApi.loading || getPageSectionsApi.loading) ? '400px' : 'auto' }}>
            {(listPagesApi.loading || getPageSectionsApi.loading) && allSections.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedSections.map(sectionItem => (
              <div key={sectionItem.id} className="item-card" onClick={() => handleEdit(sectionItem)}>
                <div className="item-card-body">
                  <div className="item-id">#{sectionItem.id}</div>
                  <h3 className="item-title">{getLocalizedField(sectionItem, 'title') || 'Untitled Section'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {getPageName(sectionItem.page_id)}
                    </span>
                    {sectionItem.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(sectionItem)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(sectionItem.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SectionsTab;
